/** Normalised shape every view consumes; built from GraphQL nodes by `normalizeIssue`. */
export type WorkIssue = {
  id: string
  number: number
  title: string
  url: string
  state: 'OPEN' | 'CLOSED'
  closedAt: string | null
  stateReason: string | null
  /** GitHub native issue type name, or null when untyped. */
  typeName: string | null
  body: string
  createdAt: string
  updatedAt: string
  author: string | null
  authorAvatarUrl: string | null
  labels: string[]
  assignees: Assignee[]
  comments: number
  /** >0 ⇒ this is a parent/epic — Sprint belongs on its children. */
  subIssueCount: number

  // Org-level Issue Fields (issue_fields preview); null when unset.
  stage: string | null
  priority: string | null
  sprint: string | null
  quarter: string | null
  effort: string | null
  product: string | null
  workload: string | null
  client: string | null
  source: string | null
  /** Verification owner (a @n3oltd/verifiers member); defaulted from Source, overridable. */
  verifier: string | null
  targetDate: string | null
  promotedAt: string | null
  /** Legacy single-gate date; empty once the org migrates to two gates. */
  releasedAt: string | null
  releasedToQaAt: string | null
  releasedToProdAt: string | null

  /** PRs that close this issue (closing keyword); for the at-a-glance indicator. */
  linkedPrs: LinkedPr[]
}

export type LinkedPr = {
  number: number
  title: string
  state: 'OPEN' | 'CLOSED' | 'MERGED'
  isDraft: boolean
  url: string
  repo: string
}

/**
 * Single source of truth mapping each org Issue Field to its WorkIssue key and
 * kind. normalizeIssue, the mutation field-name map, and the Sheet's field rows
 * all derive from this — add a field here and the three stay in lockstep.
 */
export const ISSUE_FIELDS = {
  Stage: { key: 'stage', kind: 'select' },
  Priority: { key: 'priority', kind: 'select' },
  Sprint: { key: 'sprint', kind: 'select' },
  Quarter: { key: 'quarter', kind: 'select' },
  Effort: { key: 'effort', kind: 'select' },
  Product: { key: 'product', kind: 'select' },
  Workload: { key: 'workload', kind: 'select' },
  Client: { key: 'client', kind: 'select' },
  Source: { key: 'source', kind: 'select' },
  Verifier: { key: 'verifier', kind: 'select' },
  'Target date': { key: 'targetDate', kind: 'date' },
  'Promoted at': { key: 'promotedAt', kind: 'date' },
  'Released at': { key: 'releasedAt', kind: 'date' },
  'Released to QA on': { key: 'releasedToQaAt', kind: 'date' },
  'Released to Prod on': { key: 'releasedToProdAt', kind: 'date' },
} as const satisfies Record<string, { key: keyof WorkIssue; kind: 'select' | 'date' }>

export type IssueFieldName = keyof typeof ISSUE_FIELDS

export type Assignee = {
  login: string
  name: string | null
  avatarUrl: string
}

export type FieldOption = {
  id: string
  name: string
  color: string | null
}

export type IssueField = {
  id: string
  name: string
  dataType: 'single-select' | 'number' | 'date' | 'text'
  options: FieldOption[]
}

/** Raw GraphQL node shapes (only what we select). */
export type IssueFieldValueNode = {
  __typename: string
  value?: number | string
  name?: string
  optionId?: string
  field?: { name?: string }
}

export type IssueNode = {
  id: string
  number: number
  title: string
  url: string
  state: 'OPEN' | 'CLOSED'
  closedAt?: string | null
  stateReason?: string | null
  body: string | null
  createdAt: string
  updatedAt: string
  author: { login: string; avatarUrl: string } | null
  issueType: { name: string } | null
  labels: { nodes: Array<{ name: string }> } | null
  assignees: { nodes: Array<{ login: string; name: string | null; avatarUrl: string }> } | null
  comments: { totalCount: number } | null
  subIssues?: { totalCount: number } | null
  issueFieldValues: { nodes: IssueFieldValueNode[] } | null
  linkedPrs?: {
    nodes: Array<{
      number: number
      title: string
      state: 'OPEN' | 'CLOSED' | 'MERGED'
      isDraft: boolean
      url: string
      repository: { nameWithOwner: string }
    }>
  } | null
}

export function normalizeIssue(node: IssueNode): WorkIssue {
  const fields: Record<string, IssueFieldValueNode> = {}
  for (const fv of node.issueFieldValues?.nodes ?? []) {
    const name = fv.field?.name
    if (name) fields[name] = fv
  }

  const issue = {
    id: node.id,
    number: node.number,
    title: node.title,
    url: node.url,
    state: node.state,
    closedAt: node.closedAt ?? null,
    stateReason: node.stateReason ?? null,
    typeName: node.issueType?.name ?? null,
    body: node.body ?? '',
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    author: node.author?.login ?? null,
    authorAvatarUrl: node.author?.avatarUrl ?? null,
    labels: (node.labels?.nodes ?? []).map((l) => l.name),
    assignees: (node.assignees?.nodes ?? []).map((a) => ({
      login: a.login,
      name: a.name,
      avatarUrl: a.avatarUrl,
    })),
    comments: node.comments?.totalCount ?? 0,
    subIssueCount: node.subIssues?.totalCount ?? 0,
    stage: null,
    priority: null,
    sprint: null,
    quarter: null,
    effort: null,
    product: null,
    workload: null,
    client: null,
    source: null,
    verifier: null,
    targetDate: null,
    promotedAt: null,
    releasedAt: null,
    releasedToQaAt: null,
    releasedToProdAt: null,
    linkedPrs: (node.linkedPrs?.nodes ?? []).map((pr) => ({
      number: pr.number,
      title: pr.title,
      state: pr.state,
      isDraft: pr.isDraft,
      url: pr.url,
      repo: pr.repository.nameWithOwner,
    })),
  } satisfies WorkIssue

  // Populate the Issue Field values from the single field-definition table.
  for (const [name, def] of Object.entries(ISSUE_FIELDS)) {
    const fv = fields[name]
    if (!fv) continue
    if (def.kind === 'select') {
      ;(issue[def.key] as string | null) = fv.name ?? null
    } else {
      ;(issue[def.key] as string | null) = typeof fv.value === 'string' ? fv.value : null
    }
  }

  return issue
}

/** "When did this go live" across both release models. */
export function liveReleasedAt(issue: Pick<WorkIssue, 'releasedAt' | 'releasedToProdAt'>): string | null {
  return issue.releasedToProdAt ?? issue.releasedAt
}
